import { calculateAspectRatio } from "helpers/utility/utilities";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import RelevanceTypeIcon from "views/uikit/RelevanceTypeIcon";
import { useEffect, useRef, useState } from "react";
import BoundingBox from "./BoundingBox";
import { RelevanceType } from "types";
import classNames from "classnames";

import styles from "./style.module.scss";

const ObjectPreview = ({
  src,
  labels,
  boundingBoxes,
  showAssetPreview,
  relevanceType = null,
  handleMouseEnter,
  handleMouseLeave,
}: ObjectPreviewProps) => {
  const [loading, setLoading] = useState(true);

  const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
    height: 0,
    width: 0,
  });

  const [boundingBoxContainerDimensions, setBoundingBoxContainerDimensions] =
    useState<ImageDimensions>({
      height: 0,
      width: 0,
    });

  const objectImageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const showRelevance = relevanceType && !loading;

  const objectImageWrapperClassnames = classNames(styles.objectImageWrapper, {
    [styles.objectImageWrapperForAssetPreview]: showAssetPreview,
  });

  const calculateNewImageDimensions = () => {
    if (objectImageRef.current) {
      let newValues = calculateAspectRatio(objectImageRef.current);
      setBoundingBoxContainerDimensions({
        height: newValues[0],
        width: newValues[1],
      });
    }
  };

  const handleLoad = () => {
    setLoading(false);
    calculateNewImageDimensions();
    setImageDimensions({
      height: objectImageRef.current?.naturalHeight as number,
      width: objectImageRef.current?.naturalWidth as number,
    });
  };

  const boundingBoxContainerStyle = () => {
    if (
      boundingBoxContainerDimensions.height > 0 &&
      boundingBoxContainerDimensions.width > 0
    ) {
      return {
        height: `${boundingBoxContainerDimensions.height}px`,
        width: `${boundingBoxContainerDimensions.width}px`,
      };
    } else {
      return undefined;
    }
  };

  useEffect(() => {
    calculateNewImageDimensions();
    const objectImageElement = objectImageRef.current;
    if (!objectImageElement) return;

    const resizeObserver = new ResizeObserver(calculateNewImageDimensions);
    resizeObserver.observe(objectImageElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [objectImageRef, src]);

  return (
    <ScrollAwareHover
      ref={containerRef}
      className={objectImageWrapperClassnames}
      onMouseOver={() => showAssetPreview && handleMouseEnter()}
      onMouseLeave={() => showAssetPreview && handleMouseLeave()}
    >
      <>
        <div
          className={styles.boundingBoxContainer}
          style={boundingBoxContainerStyle()}
        >
          {boundingBoxes.map((item: number[], index: number) => {
            const singleLabel = labels ? labels[index] || undefined : undefined;
            return (
              <BoundingBox
                key={index}
                bbValues={item}
                label={singleLabel}
                isLoading={loading}
                imageDimensions={imageDimensions}
                bbContainerDimensions={boundingBoxContainerDimensions}
              />
            );
          })}
        </div>

        {showRelevance && (
          <div className={styles.relevance}>
            <RelevanceTypeIcon type={relevanceType} />
          </div>
        )}

        <img
          src={src}
          ref={objectImageRef}
          data-testid="object-image"
          className={styles.objectImage}
          alt="object img"
          loading="lazy"
          onLoad={handleLoad}
        />
      </>
    </ScrollAwareHover>
  );
};

export default ObjectPreview;

interface ObjectPreviewProps {
  src: string;
  labels: string[];
  boundingBoxes: number[][];
  showAssetPreview: boolean;
  relevanceType?: RelevanceType | null;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

type ImageDimensions = { height: number; width: number };
